import { getMarkClassname, isDisabled, marks, Statuses } from '../utils/utils';
import { FC, ReactElement, useEffect, useState } from 'react';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'src/components/Dropdown';
import { SelectItem } from 'primereact/selectitem';
import styles from '../AttendantPage.module.scss';
import { DataTable } from 'primereact/datatable';
import { classNames } from 'primereact/utils';
import tableStyles from './Table.module.scss';
import { Column } from 'primereact/column';
import { useDebounce } from 'src/hooks';
import { map, size } from 'lodash';
import { Rating } from './Rating';
import {
    AttendanceStatus,
    AttendanceStatuses,
    RawStudentAttendance,
    StudentAttendance,
} from 'src/@types/modules/attendance/attendance.types';

interface AttendanceTableProps {
    attendance: StudentAttendance[];
    disabled?: boolean;
    statusLoading: boolean;
    //
    onStatusSelected?: (
        status: AttendanceStatus,
        attendance: RawStudentAttendance
    ) => void;
    //
    onMarkSelected?: (mark: number, attendance: RawStudentAttendance) => void;
    onRewardSelected?: (
        reward: number,
        attendance: RawStudentAttendance
    ) => void;
    onCommentChange?: (
        value: string | null,
        attendance: RawStudentAttendance
    ) => void;
    //
    onAttendanceChange?: (attendance: RawStudentAttendance) => void;

    statuses?: {
        availableValues?: AttendanceStatuses[];
        labels?: Partial<Record<AttendanceStatuses, string>>;
    };
}

type CommentInputProps = {
    disabled?: boolean;
    attendance: StudentAttendance;
    onCommentChange: (value: string, attendance: StudentAttendance) => void;
};

const CommentInput: FC<CommentInputProps> = (
    props: CommentInputProps
): ReactElement => {
    const { attendance, disabled, onCommentChange } = props;

    const [inputValue, debouncedValue, setInputValue] = useDebounce(
        attendance.comment ?? '',
        400
    );

    const [wasInit, setWasInit] = useState(false);

    useEffect((): void => {
        if (wasInit && debouncedValue !== attendance.comment) {
            onCommentChange(debouncedValue, attendance);
        }

        setWasInit(true);
    }, [debouncedValue]);

    useEffect((): void => {
        if (attendance.status.id !== AttendanceStatuses.Absent) return;

        setInputValue('');
    }, [attendance]);

    return (
        <InputTextarea
            className={classNames('w-full', {
                [tableStyles.disabledField]: disabled || isDisabled(attendance),
            })}
            value={inputValue}
            disabled={disabled || isDisabled(attendance)}
            onChange={event => setInputValue(event.target.value)}
        />
    );
};

export const DEFAULT_AVAILABLE_VALUES: AttendanceStatuses[] = [
    AttendanceStatuses.Present,
    AttendanceStatuses.Late,
    AttendanceStatuses.Absent,
];

export const DEFAULT_STATUS_LABELS: Record<AttendanceStatuses, string> = {
    [AttendanceStatuses.Present]: 'Присутствует',
    [AttendanceStatuses.Late]: 'Опаздывает',
    [AttendanceStatuses.Absent]: 'Отсутствует',
    [AttendanceStatuses.NotMarked]: 'Не отмечен',
};

export const AttendanceTable: FC<AttendanceTableProps> = (
    props: AttendanceTableProps
): ReactElement => {
    const { disabled, attendance, statusLoading } = props;

    const { availableValues = DEFAULT_AVAILABLE_VALUES, labels = {} } =
        props.statuses ?? {};

    const statusLabels: Record<AttendanceStatuses, string> = Object.assign(
        DEFAULT_STATUS_LABELS,
        labels
    );

    const updateAttendance = <Field extends keyof RawStudentAttendance>(
        attendance: StudentAttendance,
        field: Field,
        value: RawStudentAttendance[Field]
    ): RawStudentAttendance => {
        const updatedAttendance: RawStudentAttendance = {
            ...{
                ...attendance,
                status: attendance.status.id,
            },
            [field]: value,
        };

        if (props.onAttendanceChange)
            props.onAttendanceChange(updatedAttendance);

        return updatedAttendance;
    };

    const onStatusSelected = (
        event: DropdownChangeEvent,
        attendance: StudentAttendance
    ): void => {
        const updatedAttendance: RawStudentAttendance = updateAttendance(
            attendance,
            'status',
            event.value
        );

        if (!props.onStatusSelected) return;

        return props.onStatusSelected(event.value, updatedAttendance);
    };

    const onMarkSelected = (
        event: DropdownChangeEvent,
        attendance: StudentAttendance
    ): void => {
        const updatedAttendance: RawStudentAttendance = updateAttendance(
            attendance,
            'mark',
            event.value
        );

        if (!props.onMarkSelected) return;

        return props.onMarkSelected(event.value, updatedAttendance);
    };

    const onRewardSelected = (
        reward: number,
        attendance: StudentAttendance
    ): void => {
        const updatedAttendance: RawStudentAttendance = updateAttendance(
            attendance,
            'reward',
            reward
        );

        if (!props.onRewardSelected) return;

        return props.onRewardSelected(reward, updatedAttendance);
    };

    const onCommentChange = (
        comment: string,
        attendance: StudentAttendance
    ): void => {
        const formattedComment: string | null = size(comment.trim())
            ? comment
            : null;

        const updatedAttendance: RawStudentAttendance = updateAttendance(
            attendance,
            'comment',
            formattedComment
        );

        if (!props.onCommentChange) return;

        return props.onCommentChange(formattedComment, updatedAttendance);
    };

    const getAttendanceStatusSelectorValue = (
        status: AttendanceStatuses
    ): AttendanceStatuses | null => {
        if (status === AttendanceStatuses.NotMarked) return null;
        return status;
    };

    return (
        <DataTable
            className={styles.tableWrapper}
            tableClassName={styles.table}
            value={attendance}
        >
            <Column field={'fullname'} header={'Студент'} align={'left'} />
            <Column
                field={'status'}
                header={'Статус'}
                align={'left'}
                style={{ minWidth: 204 }}
                body={(attendance: StudentAttendance): ReactElement => {
                    return (
                        <Dropdown
                            disabled={disabled}
                            loading={statusLoading}
                            value={getAttendanceStatusSelectorValue(
                                attendance.status.id
                            )}
                            placeholder={'Не отмечен'}
                            className={classNames(
                                'w-full',
                                Statuses[attendance.status.id]?.className,
                                {
                                    [tableStyles.disabledField]: disabled,
                                }
                            )}
                            onChange={(event: DropdownChangeEvent): void =>
                                onStatusSelected(event, attendance)
                            }
                            options={map(
                                availableValues,
                                (status: AttendanceStatuses): SelectItem => ({
                                    label: statusLabels[status],
                                    value: status,
                                })
                            )}
                        />
                    );
                }}
            />
            <Column
                header={'Работа в классе'}
                style={{ minWidth: 230 }}
                align={'left'}
                body={(attendance: StudentAttendance): ReactElement => {
                    return (
                        <Dropdown
                            value={attendance.mark}
                            className={classNames(
                                'w-full',
                                getMarkClassname(attendance.mark),
                                {
                                    [tableStyles.disabledField]:
                                        disabled || isDisabled(attendance),
                                }
                            )}
                            disabled={disabled || isDisabled(attendance)}
                            onChange={event =>
                                onMarkSelected(event, attendance)
                            }
                            options={map(
                                marks,
                                (mark: string, index: number) => ({
                                    value: index,
                                    label: mark,
                                    className: getMarkClassname(index),
                                })
                            )}
                        />
                    );
                }}
            />
            <Column
                field={'reward'}
                header={'Награда'}
                style={{ width: 142 }}
                align={'left'}
                body={(attendance: StudentAttendance): ReactElement => {
                    return (
                        <Rating
                            className={classNames({
                                [tableStyles.disabledField]:
                                    disabled || isDisabled(attendance),
                            })}
                            disabled={disabled || isDisabled(attendance)}
                            value={attendance.reward}
                            onChange={(reward: number): void =>
                                onRewardSelected(reward, attendance)
                            }
                        />
                    );
                }}
            />
            <Column
                field={'comment'}
                header={'Комментарий'}
                align={'left'}
                body={(attendance: StudentAttendance): ReactElement => (
                    <CommentInput
                        key={attendance.id}
                        disabled={disabled}
                        attendance={attendance}
                        onCommentChange={onCommentChange}
                    />
                )}
            />
        </DataTable>
    );
};
